import { useState } from "react";
import { Shield, Fingerprint, Lock, Eye, EyeOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface LockScreenProps {
  onUnlock: () => Promise<boolean> | boolean;
  userEmail?: string;
}

export const LockScreen = ({ onUnlock, userEmail }: LockScreenProps) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [method, setMethod] = useState<"biometric" | "password">("biometric");

  const handleBiometricUnlock = async () => {
    setLoading(true);
    setError("");
    try {
      // Check if WebAuthn is available
      if (window.PublicKeyCredential) {
        const available =
          await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        if (available) {
          // Request platform authenticator (Face ID / fingerprint)
          const credential = await navigator.credentials.get({
            publicKey: {
              challenge: crypto.getRandomValues(new Uint8Array(32)),
              timeout: 60000,
              userVerification: "required",
              rpId: window.location.hostname,
            },
          });
          if (credential) {
            const success = await onUnlock();
            if (!success) setError("Sessão expirada. Faça login novamente.");
            return;
          }
        }
      }
      // Fallback to password
      setMethod("password");
    } catch {
      setMethod("password");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUnlock = async () => {
    if (!password || !userEmail) return;
    setLoading(true);
    setError("");
    try {
      const { error: authError } = await supabase.auth.signInWithPassword({
        email: userEmail,
        password,
      });
      if (authError) {
        setError("Senha incorreta. Tente novamente.");
        setPassword("");
        return;
      }
      const success = await onUnlock();
      if (!success) setError("Erro ao desbloquear. Tente novamente.");
    } catch {
      setError("Erro de autenticação.");
    } finally {
      setLoading(false);
      setPassword("");
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center p-6">
      <div className="w-full max-w-sm space-y-8 text-center">
        {/* Shield icon */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield size={48} className="text-primary" />
            </div>
            <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-destructive flex items-center justify-center">
              <Lock size={14} className="text-destructive-foreground" />
            </div>
          </div>
          <div>
            <h1 className="text-xl font-bold text-foreground">
              Sessão Bloqueada
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Por segurança, sua sessão foi bloqueada após inatividade.
            </p>
          </div>
        </div>

        {method === "biometric" ? (
          <div className="space-y-4">
            <button
              onClick={handleBiometricUnlock}
              disabled={loading}
              className="w-full py-4 rounded-2xl bg-primary text-primary-foreground font-bold flex items-center justify-center gap-3 hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
              ) : (
                <>
                  <Fingerprint size={22} />
                  Desbloquear com Biometria
                </>
              )}
            </button>
            <button
              onClick={() => setMethod("password")}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Usar senha em vez disso
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handlePasswordUnlock()}
                placeholder="Digite sua senha"
                autoFocus
                className="w-full px-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary pr-12"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground"
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            <button
              onClick={handlePasswordUnlock}
              disabled={loading || !password}
              className="w-full py-3 rounded-xl bg-primary text-primary-foreground font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin mx-auto" />
              ) : (
                "Desbloquear"
              )}
            </button>
            {window.PublicKeyCredential && (
              <button
                onClick={() => setMethod("biometric")}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Usar biometria
              </button>
            )}
          </div>
        )}

        {error && (
          <p className="text-sm text-destructive font-medium">{error}</p>
        )}

        <p className="text-xs text-muted-foreground">
          Protegido por criptografia AES-256-GCM • Zero-Trust Architecture
        </p>
      </div>
    </div>
  );
};
